import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, usePublicConfig } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import StatusHistoryModal from "../components/StatusHistoryModal";
import ReviewPanel from "../components/ReviewPanel";
import {
  User,
  LogOut,
  Clock,
  MapPin,
  Car,
  Calendar,
  ArrowRight,
  Moon,
  Home,
  ChevronRight,
  Plane,
  XCircle,
  AlertTriangle,
  CheckCircle2,
  History,
  CreditCard,
  MessageCircle,
  Ship
} from "lucide-react";
import { cleanWhatsAppDigits } from "../components/WhatsAppButton";

const STATUS_STYLES = {
  pending: "status-pending",
  confirmed: "status-confirmed",
  awaiting_payment: "bg-orange-500/15 border border-orange-500/30 text-orange-400",
  cancelled: "status-cancelled",
  refunded: "bg-pink-500/15 border border-pink-500/30 text-pink-400",
  completed: "status-completed",
  proposal_sent: "bg-violet-500/15 border border-violet-500/30 text-violet-400",
  proposal_accepted: "bg-teal-500/15 border border-teal-500/30 text-teal-400"
};

function pickupDateTime(quote) {
  if (!quote?.pickup_date || !quote?.pickup_time) return null;
  const dt = new Date(`${quote.pickup_date}T${quote.pickup_time}:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatRemaining(ms) {
  if (ms <= 0) return "Cancellation window closed";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes - days * 60 * 24 - hours * 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(" ");
}

function PayNowPanel({ quote }) {
  // Show only when an invoice is awaiting payment and not already settled.
  if (quote.status !== "awaiting_payment" || quote.paid) return null;
  return (
    <div
      className="mt-4 border-t border-white/5 pt-4 flex flex-wrap items-center justify-between gap-3"
      data-testid={`pay-now-panel-${quote.quote_id}`}
    >
      <p className="text-orange-300 text-xs flex items-center gap-2">
        <CreditCard className="w-4 h-4" />
        Payment is due to confirm this booking — €{Number(quote.total_price || 0).toFixed(2)}
      </p>
      <Link
        to={`/pay/${quote.quote_id}`}
        className="inline-flex items-center gap-1 bg-[#C5A059] hover:bg-[#B8903F] text-black text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors"
        data-testid={`pay-now-btn-${quote.quote_id}`}
      >
        <CreditCard className="w-4 h-4" />
        Pay now
      </Link>
    </div>
  );
}

function ProposalAcceptPanel({ quote, onAccepted }) {
  const [submitting, setSubmitting] = useState(false);

  if (quote.status !== "proposal_sent") return null;

  const accept = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${quote.quote_id}/accept-proposal`);
      toast.success("Proposal accepted! Our team will confirm shortly.");
      onAccepted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept proposal");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-4 border-t border-white/5 pt-4 flex flex-wrap items-center justify-between gap-3" data-testid={`proposal-accept-${quote.quote_id}`}>
      <p className="text-violet-300 text-xs flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> An updated proposal is awaiting your confirmation.
      </p>
      <Button
        size="sm"
        onClick={accept}
        disabled={submitting}
        className="bg-violet-600 hover:bg-violet-700 text-white"
        data-testid={`accept-proposal-${quote.quote_id}`}
      >
        <CheckCircle2 className="w-4 h-4 mr-1" />
        {submitting ? "Accepting..." : "Confirm the proposal"}
      </Button>
    </div>
  );
}

function CancellationPanel({ quote, onCancelled, waDisplay }) {
  const [now, setNow] = useState(Date.now());
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const pickupDt = pickupDateTime(quote);
  if (!pickupDt) return null;
  const cutoff = pickupDt.getTime() - 24 * 60 * 60 * 1000;
  const remaining = cutoff - now;
  const open = remaining > 0;

  // Only confirmed / awaiting_payment can be cancelled
  if (!["confirmed", "awaiting_payment"].includes(quote.status)) return null;

  const cancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${quote.quote_id}/cancel`);
      toast.success("Booking cancelled. We have sent you a confirmation email.");
      onCancelled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/5 pt-4" data-testid={`cancel-panel-${quote.quote_id}`}>
      {open ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-[#C5A059]" />
            <span className="text-white/60">Free cancellation in:</span>
            <span className="font-mono text-[#C5A059] font-semibold tabular-nums" data-testid={`countdown-${quote.quote_id}`}>
              {formatRemaining(remaining)}
            </span>
          </div>
          {!confirming ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(true)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`cancel-btn-${quote.quote_id}`}
            >
              <XCircle className="w-4 h-4 mr-1" /> Cancel booking
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs">Are you sure?</span>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} className="text-white/60">No</Button>
              <Button
                size="sm"
                onClick={cancel}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid={`confirm-cancel-${quote.quote_id}`}
              >
                {submitting ? "Cancelling..." : "Yes, cancel"}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-amber-400/80 text-xs" data-testid={`cancel-locked-${quote.quote_id}`}>
          <AlertTriangle className="w-4 h-4" />
          <span>Cancellation window has passed (less than 24h before pickup). Please contact us via WhatsApp {waDisplay}.</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, login } = useAuth();
  const { whatsapp_number } = usePublicConfig();
  const waDisplay = whatsapp_number || "+39 393 907 8663";
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyQuoteId, setHistoryQuoteId] = useState(null);

  useEffect(() => {
    // Defensive redirect — drivers who accidentally land on /dashboard (from older sessions)
    // get routed to their own calendar dashboard.
    if (user?.role === "driver") {
      navigate("/driver", { replace: true });
      return;
    }
    if (!user) return;  // wait for auth, signed-out gate shown below
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.user_id]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to load your bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Friendly login gate for guests landing on /dashboard.
  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12" data-testid="dashboard-login-gate">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center">
            <User className="w-9 h-9 text-[#C5A059]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Your Personal Page</h1>
          <p className="text-white/60 mb-2">
            Sign in to see your bookings, cancel a transfer, or contact your driver.
          </p>
          <p className="text-white/40 text-sm mb-8">
            This area is reserved for registered customers.
          </p>
          <Button
            onClick={() => login()}
            className="btn-gold w-full py-3 h-auto text-base font-semibold tracking-wide"
            data-testid="dashboard-signin-btn"
          >
            Sign in with Google
          </Button>
          <Link to="/" className="block mt-4 text-white/40 hover:text-[#C5A059] text-sm transition-colors">
            ← Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-2xl font-bold logo-text" data-testid="dashboard-logo">TN</Link>
              <nav className="hidden md:flex items-center gap-4">
                <Link to="/" className="text-white/60 hover:text-[#C5A059] text-sm flex items-center gap-1">
                  <Home className="w-4 h-4" /> Home
                </Link>
                <ChevronRight className="w-4 h-4 text-white/20" />
                <span className="text-white text-sm">Dashboard</span>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              {(user?.role === "admin" || user?.role === "super_admin") && (
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin")}
                  className="text-white/60 hover:text-[#C5A059]"
                  data-testid="admin-link"
                >
                  Admin Panel
                </Button>
              )}
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#C5A059]" />
                  </div>
                )}
                <span className="hidden sm:block text-white text-sm">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-white/60 hover:text-red-400"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12" data-testid="welcome-section">
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-white/50">Manage your transfer bookings and quotes</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Link to="/#booking" className="form-section card-hover flex items-center gap-4" data-testid="new-booking-card">
            <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <Car className="w-6 h-6 text-[#C5A059]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">New Booking</h3>
              <p className="text-white/50 text-sm">Request a transfer quote</p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#C5A059] ml-auto" />
          </Link>

          <div className="form-section flex items-center gap-4" data-testid="total-bookings-card">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{quotes.length}</h3>
              <p className="text-white/50 text-sm">Total Bookings</p>
            </div>
          </div>

          <div className="form-section flex items-center gap-4" data-testid="pending-bookings-card">
            <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#C5A059]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">
                {quotes.filter(q => q.status === "pending").length}
              </h3>
              <p className="text-white/50 text-sm">Pending Quotes</p>
            </div>
          </div>
        </div>

        <div className="form-section" data-testid="bookings-list">
          <h2 className="text-xl font-semibold text-white mb-6">Your Bookings</h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12">
              <Car className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-4">No bookings yet</p>
              <Link to="/#booking">
                <Button className="btn-gold">Book Your First Transfer</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {quotes.map((quote) => (
                <div
                  key={quote.quote_id}
                  className="bg-[#1F1F1F]/50 border border-white/5 rounded-sm p-6 hover:border-[#C5A059]/30 transition-colors"
                  data-testid={`booking-${quote.quote_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-sm text-xs uppercase tracking-wide ${STATUS_STYLES[quote.status] || "status-pending"}`}>
                          {quote.status}
                        </span>
                        {quote.is_night_service && (
                          <span className="night-badge">
                            <Moon className="w-3 h-3" /> Night
                          </span>
                        )}
                        {quote.flight_details?.flight_number && (
                          <span className="px-2 py-1 rounded-sm text-[10px] uppercase tracking-wide bg-sky-500/15 border border-sky-500/30 text-sky-400 flex items-center gap-1">
                            <Plane className="w-3 h-3" /> {quote.flight_details.flight_number}
                          </span>
                        )}
                        {quote.paid && (
                          <span className="px-2 py-1 rounded-sm text-[10px] uppercase tracking-wide bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 flex items-center gap-1" data-testid={`paid-badge-${quote.quote_id}`}>
                            <CreditCard className="w-3 h-3" /> Paid
                          </span>
                        )}
                        <button
                          onClick={() => setHistoryQuoteId(quote.quote_id)}
                          className="ml-auto text-white/40 hover:text-[#C5A059] text-[11px] uppercase tracking-wide flex items-center gap-1"
                          data-testid={`history-btn-${quote.quote_id}`}
                        >
                          <History className="w-3 h-3" /> History
                        </button>
                      </div>

                      {quote.venice_hotel_name && (
                        <div className="flex items-center gap-1.5 mb-1.5 text-[#C5A059] text-sm" data-testid={`dashboard-hotel-${quote.quote_id}`}>
                          <Ship className="w-3.5 h-3.5" />
                          <span className="font-medium">{quote.venice_hotel_name}</span>
                          <span className="text-white/40 text-xs">(water taxi + meet &amp; greet included)</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-white mb-2">
                        <MapPin className="w-4 h-4 text-[#C5A059]" />
                        <span>{quote.origin}</span>
                        <ArrowRight className="w-4 h-4 text-white/30" />
                        <span>{quote.destination}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {quote.pickup_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {quote.pickup_time}
                        </span>
                        {quote.driver_name && (
                          <span className="inline-flex items-center gap-2 text-violet-300">
                            <Car className="w-4 h-4" /> {quote.driver_name}
                            {quote.driver_phone && cleanWhatsAppDigits(quote.driver_phone) && (
                              <a
                                href={`https://wa.me/${cleanWhatsAppDigits(quote.driver_phone)}?text=${encodeURIComponent(`Hi ${quote.driver_name}, I'm ${quote.user_name} (booking ${quote.quote_id}).`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] text-[11px] font-medium px-2 py-0.5 rounded-full border border-[#25D366]/30 transition-colors"
                                data-testid={`driver-whatsapp-${quote.quote_id}`}
                                aria-label={`Contact driver ${quote.driver_name} on WhatsApp`}
                                title="Message driver on WhatsApp"
                              >
                                <MessageCircle className="w-3 h-3" /> WhatsApp
                              </a>
                            )}
                          </span>
                        )}
                      </div>

                      {(quote.flight_details?.departure_datetime || quote.flight_details?.arrival_datetime) && (
                        <div className="flex flex-wrap items-center gap-4 text-xs text-white/40 mt-2">
                          {quote.flight_details.departure_datetime && (
                            <span>Flight departure: <span className="text-white/60">{quote.flight_details.departure_datetime.replace("T", " ")}</span></span>
                          )}
                          {quote.flight_details.arrival_datetime && (
                            <span>Flight arrival: <span className="text-white/60">{quote.flight_details.arrival_datetime.replace("T", " ")}</span></span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold text-[#C5A059]">€{quote.total_price}</div>
                      <div className="text-xs text-white/40">
                        {quote.is_night_service && `incl. €${quote.night_surcharge} night surcharge`}
                      </div>
                    </div>
                  </div>

                  <ProposalAcceptPanel quote={quote} onAccepted={fetchQuotes} />
                  <PayNowPanel quote={quote} />
                  <CancellationPanel quote={quote} onCancelled={fetchQuotes} />
                  <ReviewPanel quote={quote} />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {historyQuoteId && (
        <StatusHistoryModal quoteId={historyQuoteId} onClose={() => setHistoryQuoteId(null)} />
      )}
    </div>
  );
}
